import { useState, useMemo, useCallback } from 'react'
import type {
  ForegroundTree,
  ActivityWithExchanges,
  ProjectExchange,
} from '@/lib/types/project-graph'

// StaticAttributionSankey is a store-free, read-only fork of AttributionSankey.
// It is driven entirely by props (precomputed hotspots + foregroundTree) and
// performs NO navigation, NO store upserts, and NO network calls. All node /
// flow / tooltip click handlers are inert (visual hover/pin state only).

// ─── Types ──────────────────────────────────────────────────────────────────
interface AttributedExchange {
  fg_code: string
  exchange_id: number | null
  exchange_name: string
  amount: number | null
  unit: string
  fraction: number
  kind?: 'tech' | 'biosphere'
  compartment?: string
}

export interface ContributionByActivity {
  activity_code: string
  kind: 'tech' | 'foreground'
  score: number
}

export interface HotspotWithAttribution {
  rank: number
  activity_name: string
  activity_code: string
  location: string
  database: string
  absolute_score: number
  full_score?: number // NEW: full bio impact across all cols (worker-side bio_full_per_row[b_row])
  contribution_pct: number
  unit: string
  hotspot_kind?: 'process' | 'biosphere'
  compartment?: string
  attributed_to?: Record<string, number>
  attributed_to_exchanges?: AttributedExchange[]
  contributions_by_activity?: ContributionByActivity[]
  is_residual?: boolean
}

interface FGNode {
  id: string
  label: string
  stage: string
  level: number
  parent: string | null
}

export interface ForegroundBiosphereExchange {
  fg_code: string
  bio_activity_code: string
  name: string
  amount: number
  unit: string
  compartment: string
}

interface AttributionSankeyProps {
  hotspots: HotspotWithAttribution[]
  foregroundTree: ForegroundTree
  totalScore: number
  totalUnit: string
  methodName?: string
  projectName?: string
  projectDatabase?: string
  activities?: ActivityWithExchanges[]
  foregroundBiosphereExchanges?: ForegroundBiosphereExchange[]
}

// ─── Dynamic Colour System ──────────────────────────────────────────────────
// Stage identity wears the report's VALIDATED categorical token slots
// (order is the CVD-safety mechanism — see report.css). 'direct' is the
// neutral catch-all stage, so it takes the folded-tail neutral token.
const KNOWN_COLORS: Record<string, string> = {
  manufacturing: 'var(--report-cat-1)',
  transportation: 'var(--report-cat-2)',
  use_phase: 'var(--report-cat-3)',
  end_of_life: 'var(--report-cat-4)',
  direct: 'var(--report-other)',
}
const FALLBACK_PALETTE = [
  'var(--report-cat-1)',
  'var(--report-cat-2)',
  'var(--report-cat-3)',
  'var(--report-cat-4)',
  'var(--report-cat-5)',
]

function buildStageColorMap(stages: string[]): Record<string, string> {
  const map: Record<string, string> = {}
  // Two-pass so unknown stages never collide with a known stage present in
  // the SAME chart: known stages claim their fixed slots first, then
  // fallbacks are assigned from the unclaimed slots (in palette order),
  // wrapping onto the full palette only when the free slots are exhausted.
  // Deterministic: same stage list → same colors.
  const claimed = new Set<string>()
  for (const s of stages) {
    if (KNOWN_COLORS[s]) claimed.add(KNOWN_COLORS[s])
  }
  const freeSlots = FALLBACK_PALETTE.filter((c) => !claimed.has(c))
  const fallbackPool = freeSlots.length > 0 ? freeSlots : FALLBACK_PALETTE
  let fallbackIdx = 0
  // Insert in the original stages order so downstream Object.entries
  // iteration (legend chips) keeps its previous ordering.
  for (const s of stages) {
    if (KNOWN_COLORS[s]) {
      map[s] = KNOWN_COLORS[s]
    } else {
      map[s] = fallbackPool[fallbackIdx % fallbackPool.length]
      fallbackIdx++
    }
  }
  return map
}

function prettifyStage(s: string): string {
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

// ─── Layout Constants ───────────────────────────────────────────────────────
const W = 1340,
  H = 740,
  PAD_T = 44,
  PAD_B = 12
const UH = H - PAD_T - PAD_B
const BIO_X = 0,
  BIO_W = 130 // Elementary flow column
const BIO_UP_GAP = 40 // Visual gap between EF and UP
const UP_X = BIO_X + BIO_W + BIO_UP_GAP,
  UP_W = 140 // Unit process column
const UP_FG_GAP = 40 // Gap between UP and first fg level
const FG_CW = 140,
  GAP_FG = 8,
  GAP_VERT = 3
const FU_W = 140
const FLOW_THRESHOLD = 0.001
const COL_GAP = 40
const DIRECT_FU_ID = '__DIRECT_FU__'
const ZERO_GAP = 6 // vertical gap between pos/neg stacks in EF column

function computeColumns(maxLevel: number): { fgCol: Record<number, number>; fuX: number } {
  const fuX = W - FU_W
  const fgCol: Record<number, number> = {}
  const fgStartX = UP_X + UP_W + UP_FG_GAP
  for (let lv = 1; lv <= maxLevel; lv++) {
    fgCol[lv] = fuX - lv * (FG_CW + COL_GAP)
  }
  // Clamp so fg columns don't overlap the UP column
  for (let lv = 1; lv <= maxLevel; lv++) {
    if (fgCol[lv] < fgStartX) fgCol[lv] = fgStartX
  }
  return { fgCol, fuX }
}

// ─── Helpers ────────────────────────────────────────────────────────────────
function bandPath(x0: number, y0t: number, y0b: number, x1: number, y1t: number, y1b: number) {
  const cp = x0 * 0.42 + x1 * 0.58
  return `M${x0} ${y0t} C${cp} ${y0t} ${cp} ${y1t} ${x1} ${y1t}
          L${x1} ${y1b} C${cp} ${y1b} ${cp} ${y0b} ${x0} ${y0b} Z`
}

function formatAmount(v: number): string {
  if (!isFinite(v)) return String(v)
  const abs = Math.abs(v)
  if (abs >= 1000) return v.toExponential(2)
  if (abs >= 1) return v.toFixed(2)
  if (abs >= 0.01) return v.toFixed(3)
  if (abs > 0) return v.toExponential(2)
  return '0'
}

function fmtScore(v: number): string {
  if (v === 0) return '0'
  if (Math.abs(v) >= 1000) return v.toLocaleString(undefined, { maximumFractionDigits: 1 })
  if (Math.abs(v) >= 1) return v.toFixed(2)
  return v.toFixed(4)
}

function fmtScoreSigned(v: number, unit: string): string {
  if (v === 0) return `0 ${unit}`
  // U+2212 minus matches the existing label style for negative bios.
  const sign = v > 0 ? '+' : '−'
  return `${sign}${fmtScore(Math.abs(v))} ${unit}`
}

// ─── Convert server ForegroundTree to internal FGNode list ──────────────────
function buildFGNodes(tree: ForegroundTree): {
  fgNodes: FGNode[]
  leafFG: Set<string>
  maxLevel: number
} {
  const nodes: FGNode[] = tree.nodes.map((n) => ({
    id: n.code,
    label: n.name,
    stage: n.stage || 'unknown',
    level: n.level,
    parent: n.parent_code,
  }))
  const leafFG = new Set(tree.leaf_codes)
  return { fgNodes: nodes, leafFG, maxLevel: tree.max_level }
}

// ─── Layout Hook ────────────────────────────────────────────────────────────
interface ActiveSelection {
  type: 'bg' | 'fg' | 'flow'
  id: string
}

// All descendants including self, level >= 1 only. Used for weight aggregation.
function collectDescendantsAll(nodeId: string, fgNodes: FGNode[]): string[] {
  const self = fgNodes.find((n) => n.id === nodeId)
  const result: string[] = []
  if (self && self.level >= 1) result.push(nodeId)
  const children = fgNodes.filter((n) => n.parent === nodeId)
  for (const c of children) result.push(...collectDescendantsAll(c.id, fgNodes))
  return result
}

// ─── Items derived from hotspots ────────────────────────────────────────────
interface BioItem {
  id: string // activity_code
  name: string
  compartment: string
  score: number
  full: number
  pct: number
  isResidual?: boolean
}

interface TechItem {
  id: string // activity_code
  name: string
  loc: string
  score: number
  pct: number
  attrToFg: Record<string, number> // fgId (may be synthetic) → fraction
}

function buildItems(
  hotspots: HotspotWithAttribution[],
  fgNodeIds: Set<string>,
  validFgTargets: Set<string>,
): { bioItems: BioItem[]; techItems: TechItem[] } {
  const bioItems: BioItem[] = []
  const techItems: TechItem[] = []

  for (const h of hotspots) {
    if (h.hotspot_kind === 'biosphere') {
      bioItems.push({
        id: h.activity_code,
        name: h.activity_name,
        compartment: h.compartment ?? '',
        score: h.absolute_score,
        full: h.full_score ?? h.absolute_score,
        pct: h.contribution_pct,
        isResidual: h.is_residual === true,
      })
    } else {
      // Process hotspot — foreground ones are absorbed into fg tree, skip them here.
      if (fgNodeIds.has(h.activity_code)) continue
      if (!h.attributed_to || Object.keys(h.attributed_to).length === 0) continue
      const attrToFg: Record<string, number> = {}
      for (const [code, frac] of Object.entries(h.attributed_to)) {
        if (validFgTargets.has(code) && frac > FLOW_THRESHOLD) {
          attrToFg[code] = frac
        }
      }
      if (Object.keys(attrToFg).length === 0) continue
      techItems.push({
        id: h.activity_code,
        name: h.activity_name,
        loc: h.location,
        score: h.absolute_score,
        pct: h.contribution_pct,
        attrToFg,
      })
    }
  }

  return { bioItems, techItems }
}

// ─── Layout (bio + tech + fg + FU) ──────────────────────────────────────────
function useLayout(
  fgNodes: FGNode[],
  bioItems: BioItem[],
  techItems: TechItem[],
  bioToTechMap: Map<string, Array<{ techId: string; score: number }>>,
  bioToFgMap: Map<string, Array<{ fgSynthId: string; score: number }>>,
  maxLevel: number,
) {
  return useMemo(() => {
    if (fgNodes.length === 0) return null
    if (bioItems.length === 0 && techItems.length === 0) return null

    const { fgCol, fuX } = computeColumns(maxLevel)

    // The EF column anchors the diagram height: Σ EF score = total impact
    // (100%). Σ UP score = total impact − direct-fg EFs that bypass UP. So
    // UP column total height = UH × (Σ tech / Σ bio), which leaves a visible
    // gap below UP when direct-fg EFs exist (ecosemantic-9u1 follow-up).
    // Render every requested top-N item, never silently drop the long-tail.
    // Items get a small floor (2px) to remain visible; floored heights then
    // renormalize to fill exactly drawableH (ecosemantic-nyf — Carlos saw
    // tech column capped at ~25 even when top_n=50 because the previous
    // 5px pre-renormalization filter dropped items whose proportional
    // height fell below the floor before the floor itself was applied).
    const MIN_BOX_H = 2
    const sumBioScoreAll = bioItems.reduce((s, x) => s + Math.abs(x.score), 0)
    const sumTechScoreAll = techItems.reduce((s, x) => s + Math.abs(x.score), 0)
    const techHeightShare = sumBioScoreAll > 0 ? Math.min(1, sumTechScoreAll / sumBioScoreAll) : 1

    const layoutColumn = <T extends { score: number }>(
      items: T[],
      availableH: number,
      startY = PAD_T,
      sizeOf: (x: T) => number = (x) => Math.abs(x.score),
    ) => {
      if (items.length === 0) return [] as Array<T & { y: number; h: number }>
      const totalGap = GAP_VERT * Math.max(0, items.length - 1)
      const drawableH = Math.max(0, availableH - totalGap)
      const usableSum = items.reduce((s, x) => s + sizeOf(x), 0)
      let raw = items.map((x) =>
        usableSum > 0
          ? Math.max(MIN_BOX_H, (sizeOf(x) / usableSum) * drawableH)
          : drawableH / items.length,
      )
      const rawSum = raw.reduce((s, h) => s + h, 0)
      if (rawSum > 0 && Math.abs(rawSum - drawableH) > 0.5) {
        const k = drawableH / rawSum
        raw = raw.map((h) => h * k)
      }
      let y = startY
      return items.map((x, i) => {
        const h = raw[i]
        const r = { ...x, y, h }
        y += h + GAP_VERT
        return r
      })
    }

    // ── BIO layout — waterfall: positive stack, 0-line, negative stack ────
    // Bio rects are sized by |full_score| (impact via every column, not just
    // visible top-N). Captured/reminder split happens inside each rect at
    // render time. Forward-compat: full_score absent ⇒ falls back to |score|.
    const sumAbsBio = bioItems.reduce((s, x) => s + Math.abs(x.full), 0)
    const posItems = [...bioItems].filter((x) => x.full >= 0).sort((a, b) => b.full - a.full)
    const negItems = [...bioItems].filter((x) => x.full < 0).sort((a, b) => a.full - b.full)
    const sumPos = posItems.reduce((s, x) => s + x.full, 0)
    const sumAbsNeg = negItems.reduce((s, x) => s + Math.abs(x.full), 0)

    // Heights proportional to each group's share of Σ|full|
    const posTotalH = sumAbsBio > 0 ? (sumPos / sumAbsBio) * UH : UH
    const negTotalH = sumAbsBio > 0 ? (sumAbsNeg / sumAbsBio) * (UH - ZERO_GAP) : 0

    const posLayout = layoutColumn(posItems, posTotalH, PAD_T, (x) => Math.abs(x.full))
    const negStartY = PAD_T + posTotalH + ZERO_GAP
    const negLayout = layoutColumn(negItems, negTotalH, negStartY, (x) => Math.abs(x.full))
    const bioLayout = [...posLayout, ...negLayout]
    const zeroLineY = PAD_T + posTotalH // y-coordinate of the 0-line

    // ── TECH layout — height = UH × (Σ tech / Σ bio) ───────────────────────
    const sortedTech = [...techItems].sort((a, b) => b.score - a.score)
    const techLayout = layoutColumn(sortedTech, UH * techHeightShare)

    // ── FG tree layout ───────────────────────────────────────────────────────
    const root = fgNodes.find((n) => n.level === 0)
    if (!root) return null

    // Weight per fg node = sum of all incoming impact (tech + bypass bio)
    const fgWeights: Record<string, number> = {}
    const allDescIds = (id: string) => collectDescendantsAll(id, fgNodes)

    // Contributions from tech items
    for (const t of sortedTech) {
      for (const [fgId, frac] of Object.entries(t.attrToFg)) {
        fgWeights[fgId] = (fgWeights[fgId] ?? 0) + frac * Math.abs(t.score)
      }
    }
    // Contributions from bypass bio flows (kind=foreground)
    for (const [, fgEntries] of bioToFgMap) {
      for (const { fgSynthId, score } of fgEntries) {
        fgWeights[fgSynthId] = (fgWeights[fgSynthId] ?? 0) + Math.abs(score)
      }
    }

    const nodeLayout: Record<string, { y: number; h: number }> = {}
    nodeLayout[root.id] = { y: PAD_T, h: UH }

    function layoutChildren(parentId: string) {
      const children = fgNodes.filter((n) => n.parent === parentId)
      if (!children.length) return
      const par = nodeLayout[parentId]
      if (!par) return
      const weights: Record<string, number> = {}
      children.forEach((c) => {
        const desc = allDescIds(c.id)
        const w = desc.reduce((s, d) => s + (fgWeights[d] ?? 0), 0)
        weights[c.id] = Math.max(w, 50)
      })
      const totalW = children.reduce((s, c) => s + weights[c.id], 0)
      const gapTotal = GAP_FG * (children.length - 1)
      let cy = par.y
      children.forEach((c) => {
        const h = Math.max(12, (weights[c.id] / totalW) * (par.h - gapTotal))
        nodeLayout[c.id] = { y: cy, h }
        cy += h + GAP_FG
        layoutChildren(c.id)
      })
    }
    layoutChildren(root.id)

    // ── bioToUp flows (EF → UP) ──────────────────────────────────────────────
    const bioOffsets: Record<string, number> = {}
    bioLayout.forEach((b) => {
      bioOffsets[b.id] = b.y
    })
    const techLeftOffsets: Record<string, number> = {}
    techLayout.forEach((t) => {
      techLeftOffsets[t.id] = t.y
    })

    const bioToUpFlows: Array<{
      id: string
      bioId: string
      techId: string
      score: number
      bioY0: number
      bioY1: number
      techY0: number
      techY1: number
    }> = []

    // Per bio entry, the box height is proportional to (Σ tech contribution
    // + Σ direct-fg contribution). Each outgoing band must consume a slice
    // proportional to its score / (combined total), so bioToUp + bypass
    // bands together exactly fill bioEntry.h (ecosemantic-9u1 follow-up).
    // Apply the same threshold the band loops use so coveredScore reflects
    // exactly the bands that will actually render. Without this filter,
    // sub-threshold contributions inflate coveredScore, the covered sub-rect
    // grows past where bands stop, and a visible gap opens between the last
    // band and the start of the "Other paths" sub-rect.
    // bioCombinedTotal is Σ|contributions| (used as the band-sizing denominator
    // so individual band widths are proportional and cumulatively fill the
    // box). Don't pre-filter by FLOW_THRESHOLD — many tiny contributions can
    // legitimately sum to a meaningful score, and dropping them deflates the
    // coverage and fakes a giant 'Other paths' sub-rect (Carlos's negative
    // bio with 95% Other paths was caused by every contribution being below
    // the 0.001 threshold individually). The existing band-render loops
    // skip individual sub-threshold bands for visual clarity but the column
    // total stays honest.
    const bioCombinedTotal: Record<string, number> = {}
    bioLayout.forEach((b) => {
      const techSum = (bioToTechMap.get(b.id) ?? []).reduce((s, e) => s + Math.abs(e.score), 0)
      const fgSum = (bioToFgMap.get(b.id) ?? []).reduce((s, e) => s + Math.abs(e.score), 0)
      bioCombinedTotal[b.id] = techSum + fgSum
    })

    // Bands attach to the captured portion only; reminder is the long-tail
    // (no bands). h_band must scale to capturedH, not bio.h. Mixed-sign bios
    // where |captured|>|full| can have capturedH > b.h (rendered honestly).
    const bioCapturedH: Record<string, number> = {}
    bioLayout.forEach((b) => {
      const denom = Math.abs(b.full) || 1
      const capturedFrac = Math.abs(b.score) / denom
      bioCapturedH[b.id] = capturedFrac * b.h
    })

    for (const bioEntry of bioLayout) {
      const entries = bioToTechMap.get(bioEntry.id) ?? []
      if (entries.length === 0) continue
      const totalCombined = bioCombinedTotal[bioEntry.id] || 0
      if (totalCombined < FLOW_THRESHOLD) continue

      const sorted = [...entries].sort((a, b) => {
        const tyA = techLayout.find((t) => t.id === a.techId)?.y ?? 0
        const tyB = techLayout.find((t) => t.id === b.techId)?.y ?? 0
        return tyA - tyB
      })

      for (const { techId, score } of sorted) {
        if (Math.abs(score) < FLOW_THRESHOLD) continue
        const tLay = techLayout.find((t) => t.id === techId)
        if (!tLay) continue
        // Size each band by its share of Σ|cba|, scaled to the captured
        // portion height (the reminder portion has no bands). For mixed-sign
        // bios, |bioEntry.score| < totalCombined, so dividing by |score|
        // would overflow the box and visually skew bands past the box bottom.
        const h_bio = (Math.abs(score) / totalCombined) * bioCapturedH[bioEntry.id]
        const incomingBioAbsScore = bioLayout.reduce((s, b) => {
          const es = bioToTechMap.get(b.id) ?? []
          const e = es.find((x) => x.techId === techId)
          return s + Math.abs(e?.score ?? 0)
        }, 0)
        const h_tech =
          incomingBioAbsScore > 0 ? (Math.abs(score) / incomingBioAbsScore) * tLay.h : 0
        bioToUpFlows.push({
          id: `bio2up_${bioEntry.id}_${techId}`,
          bioId: bioEntry.id,
          techId,
          score,
          bioY0: bioOffsets[bioEntry.id],
          bioY1: bioOffsets[bioEntry.id] + h_bio,
          techY0: techLeftOffsets[techId],
          techY1: techLeftOffsets[techId] + h_tech,
        })
        bioOffsets[bioEntry.id] += h_bio
        techLeftOffsets[techId] += h_tech
      }
    }

    // ── bypass flows (EF → fg-direct synthetic, skipping UP column) ──────────
    const fgLeftOffsets: Record<string, number> = {}
    Object.keys(nodeLayout).forEach((id) => {
      fgLeftOffsets[id] = nodeLayout[id]?.y || 0
    })

    // Reset bio offsets for bypass (continue from where bioToUp left off)
    // bioOffsets already advanced from bioToUp pass above — we continue from there.

    const bypassFlows: Array<{
      id: string
      bioId: string
      fgSynthId: string
      score: number
      bioY0: number
      bioY1: number
      fgY0: number
      fgY1: number
      fgLevel: number
      stage: string
    }> = []

    for (const bioEntry of bioLayout) {
      const entries = bioToFgMap.get(bioEntry.id) ?? []
      if (entries.length === 0) continue
      const totalCombined = bioCombinedTotal[bioEntry.id] || 0
      if (totalCombined < FLOW_THRESHOLD) continue

      // Sort by fg Y for minimal crossing
      const sorted = [...entries].sort((a, b) => {
        const fA = fgNodes.find((n) => n.id === a.fgSynthId)
        const fB = fgNodes.find((n) => n.id === b.fgSynthId)
        const yA = nodeLayout[a.fgSynthId]?.y ?? (fA ? PAD_T : 9999)
        const yB = nodeLayout[b.fgSynthId]?.y ?? (fB ? PAD_T : 9999)
        return yA - yB
      })

      for (const { fgSynthId, score } of sorted) {
        if (Math.abs(score) < FLOW_THRESHOLD) continue
        const fgNode = fgNodes.find((n) => n.id === fgSynthId)
        if (!fgNode) continue
        const fgLay = nodeLayout[fgSynthId]
        if (!fgLay) continue

        // Bypass band consumes a slice of capturedH proportional to its
        // share of Σ|cba| — same denominator the bioToUp loop uses, so
        // bands sum exactly to capturedH regardless of mixed-sign cba.
        const h_bio = (Math.abs(score) / totalCombined) * bioCapturedH[bioEntry.id]
        // h on fg side: proportional to |score| vs total |incoming| to this fgSynth
        const totalFgIncomingAbs = bioLayout.reduce((s, b) => {
          const es = bioToFgMap.get(b.id) ?? []
          const e = es.find((x) => x.fgSynthId === fgSynthId)
          return s + Math.abs(e?.score ?? 0)
        }, 0)
        const h_fg = totalFgIncomingAbs > 0 ? (Math.abs(score) / totalFgIncomingAbs) * fgLay.h : 0

        bypassFlows.push({
          id: `bypass_${bioEntry.id}_${fgSynthId}`,
          bioId: bioEntry.id,
          fgSynthId,
          score,
          bioY0: bioOffsets[bioEntry.id],
          bioY1: bioOffsets[bioEntry.id] + h_bio,
          fgY0: fgLeftOffsets[fgSynthId],
          fgY1: fgLeftOffsets[fgSynthId] + h_fg,
          fgLevel: fgNode.level,
          stage: fgNode.stage,
        })
        bioOffsets[bioEntry.id] += h_bio
        fgLeftOffsets[fgSynthId] += h_fg
      }
    }

    // ── upToFg flows (UP → fg) ────────────────────────────────────────────────
    // Reset tech right-side offsets
    const techRightOffsets: Record<string, number> = {}
    techLayout.forEach((t) => {
      techRightOffsets[t.id] = t.y
    })

    const upToFgFlows: Array<{
      id: string
      techId: string
      fgId: string
      score: number
      frac: number
      techY0: number
      techY1: number
      fgY0: number
      fgY1: number
      fgLevel: number
      isShared: boolean
      stage: string
    }> = []

    for (const tEntry of techLayout) {
      const fgContribs = Object.entries(tEntry.attrToFg)
        .filter(([fgId, frac]) => {
          const n = fgNodes.find((x) => x.id === fgId)
          return n && n.level >= 1 && frac > FLOW_THRESHOLD
        })
        .sort(([a], [b]) => {
          const yA = fgLeftOffsets[a] ?? 0
          const yB = fgLeftOffsets[b] ?? 0
          return yA - yB
        })
      const fracSum = fgContribs.reduce((s, [, v]) => s + v, 0)
      if (fracSum < FLOW_THRESHOLD) continue

      for (const [fgId, frac] of fgContribs) {
        const fgNode = fgNodes.find((n) => n.id === fgId)
        if (!fgNode) continue
        const fgLay = nodeLayout[fgId]
        if (!fgLay) continue
        const h_tech = (frac / fracSum) * tEntry.h
        const fgIncoming = techItems.reduce((s, t2) => {
          const v = t2.attrToFg[fgId] || 0
          return v > FLOW_THRESHOLD ? s + v : s
        }, 0)
        const h_fg = fgIncoming > 0 ? (frac / fgIncoming) * fgLay.h : 0
        upToFgFlows.push({
          id: `up2fg_${tEntry.id}_${fgId}`,
          techId: tEntry.id,
          fgId,
          score: frac * tEntry.score,
          frac,
          techY0: techRightOffsets[tEntry.id],
          techY1: techRightOffsets[tEntry.id] + h_tech,
          fgY0: fgLeftOffsets[fgId],
          fgY1: fgLeftOffsets[fgId] + h_fg,
          fgLevel: fgNode.level,
          isShared: fgContribs.length > 1,
          stage: fgNode.stage,
        })
        techRightOffsets[tEntry.id] += h_tech
        fgLeftOffsets[fgId] += h_fg
      }
    }

    // ── tree flows (fg parent → child) ───────────────────────────────────────
    const treeFlows: Array<{
      id: string
      parentId: string
      childId: string
      stage: string
      x0: number
      y0t: number
      y0b: number
      x1: number
      y1t: number
      y1b: number
    }> = []

    fgNodes
      .filter((n) => n.parent)
      .forEach((child) => {
        const parLay = nodeLayout[child.parent!]
        const cLay = nodeLayout[child.id]
        if (!parLay || !cLay) return
        const parLevel = fgNodes.find((n) => n.id === child.parent)?.level || 0
        const x0 = parLevel === 0 ? fuX : (fgCol[parLevel] ?? fuX) + FG_CW
        const x1 = fgCol[child.level] ?? fuX
        treeFlows.push({
          id: `tree_${child.parent}_${child.id}`,
          parentId: child.parent!,
          childId: child.id,
          stage: child.stage,
          x0,
          y0t: cLay.y,
          y0b: cLay.y + cLay.h,
          x1,
          y1t: cLay.y,
          y1b: cLay.y + cLay.h,
        })
      })

    const negBioIds = new Set(negItems.map((x) => x.id))

    return {
      bioLayout,
      techLayout,
      nodeLayout,
      bioToUpFlows,
      bypassFlows,
      upToFgFlows,
      treeFlows,
      rootId: root.id,
      fgCol,
      fuX,
      zeroLineY,
      negBioIds,
      hasNegBio: negItems.length > 0,
    }
  }, [fgNodes, bioItems, techItems, bioToTechMap, bioToFgMap, maxLevel])
}

// ─── Main Component ─────────────────────────────────────────────────────────
export function StaticAttributionSankey({
  hotspots,
  foregroundTree,
  totalScore,
  totalUnit,
  methodName,
  projectName,
  activities,
  foregroundBiosphereExchanges,
}: AttributionSankeyProps) {
  const findTechExchange = useCallback(
    (fgCode: string, upCode: string): ProjectExchange | null => {
      const act = activities?.find((a) => a.code === fgCode)
      if (!act) return null
      return (
        act.exchanges.find(
          (e) => e.exchange_type === 'technosphere' && e.input_activity_code === upCode,
        ) ?? null
      )
    },
    [activities],
  )

  const findBioExchange = useCallback(
    (fgCode: string, bioCode: string): ProjectExchange | null => {
      const act = activities?.find((a) => a.code === fgCode)
      if (!act) return null
      return (
        act.exchanges.find(
          (e) => e.exchange_type === 'biosphere' && e.input_biosphere_code === bioCode,
        ) ?? null
      )
    },
    [activities],
  )

  const findChildExchange = useCallback(
    (parentFgCode: string, childFgCode: string): ProjectExchange | null => {
      const parent = activities?.find((a) => a.code === parentFgCode)
      if (!parent) return null
      return (
        parent.exchanges.find(
          (e) => e.exchange_type === 'technosphere' && e.input_activity_code === childFgCode,
        ) ?? null
      )
    },
    [activities],
  )

  // Read-only fork: selection is inert. These no-op callbacks preserve the
  // call-sites' signatures so the render tree stays identical to the workspace
  // version, but they never touch a store or perform navigation.
  const selectExchange = useCallback((exchange: ProjectExchange) => {
    void exchange
  }, [])
  const selectFgActivity = useCallback((fgCode: string) => {
    void fgCode
  }, [])
  const selectBackgroundActivity = useCallback(
    (activityCode: string, productName: string, location?: string) => {
      void activityCode
      void productName
      void location
    },
    [],
  )

  const {
    fgNodes: rawFgNodes,
    leafFG: rawLeafFG,
    maxLevel: rawMaxLevel,
  } = useMemo(() => buildFGNodes(foregroundTree), [foregroundTree])

  // Default state: ALL parents collapsed so the user sees a clean Sankey
  // and explicitly opens the activities they want to inspect (ecosemantic-nyf).
  // The set is initialized lazily once we know the fg nodes; we use a
  // sentinel marker (null) on first render and populate on the first useMemo.
  const [collapsedParents, setCollapsedParents] = useState<Set<string> | null>(null)
  const effectiveCollapsedParents = useMemo(() => {
    if (collapsedParents !== null) return collapsedParents
    // Default = every fg node treated as collapsed.
    return new Set(rawFgNodes.map((n) => n.id))
  }, [collapsedParents, rawFgNodes])
  const toggleCollapsed = useCallback(
    (parentId: string) => {
      setCollapsedParents((prev) => {
        const base = prev ?? new Set(rawFgNodes.map((n) => n.id))
        const next = new Set(base)
        if (next.has(parentId)) next.delete(parentId)
        else next.add(parentId)
        return next
      })
    },
    [rawFgNodes],
  )

  // Inject synthetic child nodes at level+1 for every fg node with direct
  // bg attribution. Two kinds:
  //   __directx__${parentId}__${exId}  — tech bg-input synthetics (from attributed_to_exchanges kind=tech)
  //   __directb__${parentId}__bio_${bio_activity_code} — biosphere direct-emission synthetics
  //     (from contributions_by_activity kind=foreground)
  const { fgNodes, maxLevel, mappedHotspots, parentsWithDirect } = useMemo(() => {
    const directIdFor = (id: string, isRoot: boolean) => (isRoot ? DIRECT_FU_ID : `__direct__${id}`)
    const directLabelFor = (label: string, isRoot: boolean) =>
      isRoot ? 'Direct (FU)' : `${label} (direct)`
    const exchangeIdFor = (parentId: string, exId: number | null, name: string) =>
      `__directx__${parentId}__${exId ?? `n_${name}`}`
    // New id scheme for biosphere-direct synthetics
    const bioDirectIdFor = (parentId: string, bioActivityCode: string) =>
      `__directb__${parentId}__bio_${bioActivityCode}`

    const parentsDirect = new Set<string>()
    // fg activities with direct biosphere exchanges always need synthetics,
    // even if they don't appear in any hotspot's attributed_to (e.g. when
    // the chosen method has cf=0 for that flow). ecosemantic-iiu.
    const fgsWithDirectBio = new Set<string>(
      (foregroundBiosphereExchanges ?? []).map((e) => e.fg_code),
    )
    for (const fg of rawFgNodes) {
      const hasAttribution = hotspots.some(
        (h) =>
          h.attributed_to &&
          fg.id in h.attributed_to &&
          (h.attributed_to[fg.id] ?? 0) > FLOW_THRESHOLD,
      )
      const hasDirectBio = fgsWithDirectBio.has(fg.id)
      if (fg.level === 0 && !hasAttribution && !hasDirectBio) continue
      if (hasAttribution || hasDirectBio) parentsDirect.add(fg.id)
    }

    if (parentsDirect.size === 0) {
      return {
        fgNodes: rawFgNodes,
        leafFG: rawLeafFG,
        maxLevel: rawMaxLevel,
        mappedHotspots: hotspots,
        parentsWithDirect: parentsDirect,
      }
    }

    const synthetics: FGNode[] = []

    parentsDirect.forEach((parentId) => {
      const parent = rawFgNodes.find((n) => n.id === parentId)!
      const isRoot = parent.level === 0
      const isCollapsed = effectiveCollapsedParents.has(parentId)

      // Gather tech exchange rows for this parent (unchanged logic)
      const parentTechExchanges: {
        exId: string
        rawId: number | null
        name: string
        amount: number | null
        unit: string
      }[] = []
      const seenTech = new Set<string>()
      if (!isCollapsed) {
        for (const h of hotspots) {
          if (!h.attributed_to_exchanges) continue
          for (const ex of h.attributed_to_exchanges) {
            if (ex.fg_code !== parentId) continue
            if (ex.fraction <= FLOW_THRESHOLD) continue
            const kind = ex.kind ?? 'tech'
            if (kind !== 'tech') continue // biosphere handled separately below
            const key = exchangeIdFor(parentId, ex.exchange_id, ex.exchange_name)
            if (seenTech.has(key)) continue
            seenTech.add(key)
            parentTechExchanges.push({
              exId: key,
              rawId: ex.exchange_id,
              name: ex.exchange_name,
              amount: ex.amount,
              unit: ex.unit,
            })
          }
        }
      }

      // Gather bio-direct synthetics from TWO sources, taking the union
      // (ecosemantic-fmw follow-up):
      //   1) foregroundBiosphereExchanges — BW2 exchange enumeration, lets
      //      us surface flows with cf=0 in the chosen method.
      //   2) contributions_by_activity (kind='foreground') from biosphere
      //      hotspots — captures any fg col with non-zero char_inv that may
      //      not appear in the BW2 exchange list (e.g. aggregated emissions).
      // Without source 2, kind=foreground bands get filtered out at routing
      // time when no matching synth exists, deflating bioCoveredScore and
      // producing a misleading 'Other paths · 95%' on the bio box.
      const parentBioDirectEntries: {
        exId: string
        bioName: string
        compartment: string
        amount: number | null
        unit: string
      }[] = []
      const seenBio = new Set<string>()
      if (!isCollapsed) {
        // Source 1: BW2 exchange list.
        if (foregroundBiosphereExchanges) {
          for (const ex of foregroundBiosphereExchanges) {
            if (ex.fg_code !== parentId) continue
            const key = bioDirectIdFor(parentId, ex.bio_activity_code)
            if (seenBio.has(key)) continue
            seenBio.add(key)
            parentBioDirectEntries.push({
              exId: key,
              bioName: ex.name,
              compartment: ex.compartment ?? '',
              amount: ex.amount,
              unit: ex.unit,
            })
          }
        }
        // Source 2: contributions_by_activity kind=foreground from each
        // biosphere hotspot — fills any synth that source 1 missed.
        for (const h of hotspots) {
          if (h.hotspot_kind !== 'biosphere') continue
          if (!h.contributions_by_activity) continue
          for (const cba of h.contributions_by_activity) {
            if (cba.kind !== 'foreground') continue
            if (cba.activity_code !== parentId) continue
            if (Math.abs(cba.score) < FLOW_THRESHOLD) continue
            const key = bioDirectIdFor(parentId, h.activity_code)
            if (seenBio.has(key)) continue
            seenBio.add(key)
            parentBioDirectEntries.push({
              exId: key,
              bioName: h.activity_name,
              compartment: h.compartment ?? '',
              amount: null,
              unit: '',
            })
          }
        }
      }

      const hasTech = parentTechExchanges.length > 0
      const hasBio = parentBioDirectEntries.length > 0

      if (isCollapsed || (!hasTech && !hasBio)) {
        // Single (direct) synthetic — fall back to previous behavior
        const synId = directIdFor(parentId, isRoot)
        synthetics.push({
          id: synId,
          label: directLabelFor(parent.label, isRoot),
          stage: 'direct',
          level: parent.level + 1,
          parent: parentId,
        })
      } else {
        // Per-exchange tech synthetics
        parentTechExchanges.forEach((ex) => {
          const amtStr = ex.amount != null ? `${formatAmount(ex.amount)} ${ex.unit}`.trim() : ''
          const label = amtStr ? `${ex.name} • ${amtStr}` : ex.name
          synthetics.push({
            id: ex.exId,
            label,
            stage: 'direct',
            level: parent.level + 1,
            parent: parentId,
          })
        })
        // Per-biosphere-activity direct synthetics. Label shows physical
        // exchange amount + unit (e.g. "0.05 kg"), NOT the characterized
        // impact, since these synthetics exist regardless of the chosen
        // method's cf. The bypass band into the synthetic still carries the
        // characterized contribution when the bio is a top-N hotspot.
        parentBioDirectEntries.forEach((bio) => {
          const compTag = bio.compartment ? ` [${bio.compartment}]` : ''
          const amtStr = bio.amount != null ? `${formatAmount(bio.amount)} ${bio.unit}`.trim() : ''
          const label = amtStr ? `${bio.bioName}${compTag} • ${amtStr}` : `${bio.bioName}${compTag}`
          synthetics.push({
            id: bio.exId,
            label,
            stage: 'direct',
            level: parent.level + 1,
            parent: parentId,
          })
        })
        // If no tech but has bio (or vice versa), still no collapsed fallback needed above
      }
    })

    const fgNodesAug = [...rawFgNodes, ...synthetics]
    const leafFGAug = new Set(rawLeafFG)
    synthetics.forEach((s) => leafFGAug.add(s.id))
    const newMaxLevel =
      synthetics.length > 0 ? Math.max(rawMaxLevel, ...synthetics.map((s) => s.level)) : rawMaxLevel

    // Remap hotspot attributed_to from parent fg → its synthetics (tech only)
    const mapped = hotspots.map((h) => {
      if (!h.attributed_to) return h
      const newAttr = { ...h.attributed_to }
      let changed = false
      parentsDirect.forEach((parentId) => {
        if (!(parentId in newAttr)) return
        const parentNode = rawFgNodes.find((n) => n.id === parentId)!
        const isRoot = parentNode.level === 0
        const isCollapsed = effectiveCollapsedParents.has(parentId)
        const fgFraction = newAttr[parentId]

        if (isCollapsed) {
          newAttr[directIdFor(parentId, isRoot)] = fgFraction
          delete newAttr[parentId]
          changed = true
          return
        }

        // Expanded: split by per-exchange fractions (tech only in attributed_to_exchanges)
        const exRows = (h.attributed_to_exchanges || []).filter(
          (ex) =>
            ex.fg_code === parentId &&
            ex.fraction > FLOW_THRESHOLD &&
            (ex.kind ?? 'tech') === 'tech',
        )
        if (exRows.length === 0) {
          newAttr[directIdFor(parentId, isRoot)] = fgFraction
          delete newAttr[parentId]
          changed = true
          return
        }
        for (const ex of exRows) {
          const synId = exchangeIdFor(parentId, ex.exchange_id, ex.exchange_name)
          newAttr[synId] = (newAttr[synId] ?? 0) + ex.fraction
        }
        delete newAttr[parentId]
        changed = true
      })
      return changed ? { ...h, attributed_to: newAttr } : h
    })

    return {
      fgNodes: fgNodesAug,
      leafFG: leafFGAug,
      maxLevel: newMaxLevel,
      mappedHotspots: mapped,
      parentsWithDirect: parentsDirect,
    }
  }, [
    rawFgNodes,
    rawLeafFG,
    rawMaxLevel,
    hotspots,
    effectiveCollapsedParents,
    foregroundBiosphereExchanges,
  ])

  // Build sets for filtering
  const fgNodeIdSet = useMemo(() => new Set(rawFgNodes.map((n) => n.id)), [rawFgNodes])
  const fgTargets = useMemo(
    () => new Set(fgNodes.filter((n) => n.level >= 1).map((n) => n.id)),
    [fgNodes],
  )

  // Build bio/tech items from mapped hotspots
  const { bioItems, techItems } = useMemo(
    () => buildItems(mappedHotspots, fgNodeIdSet, fgTargets),
    [mappedHotspots, fgNodeIdSet, fgTargets],
  )

  // Build bioToTechMap: bioId → list of { techId, score }. Don't filter by
  // FLOW_THRESHOLD — Σ|contributions| anchors the band-sizing denominator,
  // and dropping tiny contributions deflates it and fakes 'Other paths'.
  // The band-render loops apply their own visual threshold per band.
  const bioToTechMap = useMemo(() => {
    const map = new Map<string, Array<{ techId: string; score: number }>>()
    const techIdSet = new Set(techItems.map((t) => t.id))
    for (const h of mappedHotspots) {
      if (h.hotspot_kind !== 'biosphere') continue
      if (!h.contributions_by_activity) continue
      const entries: Array<{ techId: string; score: number }> = []
      for (const cba of h.contributions_by_activity) {
        if (cba.kind !== 'tech') continue
        if (!techIdSet.has(cba.activity_code)) continue
        entries.push({ techId: cba.activity_code, score: cba.score })
      }
      if (entries.length > 0) map.set(h.activity_code, entries)
    }
    return map
  }, [mappedHotspots, techItems])

  // Build bioToFgMap: bioId → list of { fgSynthId, score } for kind=foreground.
  // Same reasoning as above — no FLOW_THRESHOLD filter at map level.
  const bioToFgMap = useMemo(() => {
    const map = new Map<string, Array<{ fgSynthId: string; score: number }>>()
    for (const h of mappedHotspots) {
      if (h.hotspot_kind !== 'biosphere') continue
      if (!h.contributions_by_activity) continue
      const entries: Array<{ fgSynthId: string; score: number }> = []
      for (const cba of h.contributions_by_activity) {
        if (cba.kind !== 'foreground') continue
        // The fgSynth id for a direct biosphere emission is:
        // __directb__${parentId}__bio_${h.activity_code}
        // where parentId = cba.activity_code (the fg activity that emits it)
        const synthId = `__directb__${cba.activity_code}__bio_${h.activity_code}`
        if (!fgTargets.has(synthId)) continue
        entries.push({ fgSynthId: synthId, score: cba.score })
      }
      if (entries.length > 0) map.set(h.activity_code, entries)
    }
    return map
  }, [mappedHotspots, fgTargets])

  const layout = useLayout(fgNodes, bioItems, techItems, bioToTechMap, bioToFgMap, maxLevel)

  const stageColorMap = useMemo(() => {
    const stages = [...new Set(fgNodes.map((n) => n.stage))]
    return buildStageColorMap(stages)
  }, [fgNodes])
  const sc = useCallback((s: string) => stageColorMap[s] || 'var(--report-other)', [stageColorMap])

  const [hov, setHov] = useState<ActiveSelection | null>(null)
  const [pinned, setPinned] = useState<ActiveSelection | null>(null)
  const [labels, setLabels] = useState(true)
  const [netHover, setNetHover] = useState(false)
  const active = pinned || hov

  // Bio items keyed by id for tooltip lookups
  const bioById = useMemo(() => {
    const m = new Map<string, BioItem>()
    bioItems.forEach((b) => m.set(b.id, b))
    return m
  }, [bioItems])
  const techById = useMemo(() => {
    const m = new Map<string, TechItem>()
    techItems.forEach((t) => m.set(t.id, t))
    return m
  }, [techItems])

  const isFlowVis = useCallback(
    (f: { bioId?: string; techId?: string; fgId?: string; fgSynthId?: string; id: string }) => {
      if (!active) return true
      if (active.type === 'bg') {
        // bg type is used for both bio and tech items
        return f.bioId === active.id || f.techId === active.id
      }
      if (active.type === 'fg') {
        return (
          f.fgId === active.id ||
          f.fgSynthId === active.id ||
          f.bioId === active.id ||
          f.techId === active.id
        )
      }
      if (active.type === 'flow') return f.id === active.id
      return true
    },
    [active],
  )

  const isTreeVis = useCallback(
    (tf: { parentId: string; childId: string }) => {
      if (!active) return true
      if (active.type === 'fg') return tf.parentId === active.id || tf.childId === active.id
      return true
    },
    [active],
  )

  const hover = useCallback(
    (t: ActiveSelection['type'], id: string) => {
      if (!pinned) setHov({ type: t, id })
    },
    [pinned],
  )
  const unhover = useCallback(() => {
    if (!pinned) setHov(null)
  }, [pinned])
  const click = useCallback((t: ActiveSelection['type'], id: string) => {
    setPinned((p) => (p?.type === t && p.id === id ? null : { type: t, id }))
    setHov(null)
  }, [])

  const tooltip = useMemo(() => {
    if (!active) return null
    if (active.type === 'bg') {
      // Could be bio or tech
      const bio = bioById.get(active.id)
      if (bio) {
        const techEntries = bioToTechMap.get(bio.id) ?? []
        const fgEntries = bioToFgMap.get(bio.id) ?? []
        return { kind: 'bio' as const, ...bio, techEntries, fgEntries }
      }
      const tech = techById.get(active.id)
      if (tech) {
        const leafBreakdown = Object.entries(tech.attrToFg)
          .filter(([id, v]) => fgTargets.has(id) && v > FLOW_THRESHOLD)
          .map(([id, frac]) => ({
            id,
            frac,
            stage: fgNodes.find((n) => n.id === id)?.stage || 'unknown',
            label: fgNodes.find((n) => n.id === id)?.label || id,
          }))
          .sort((a, b) => b.frac - a.frac)
        const fracSum = leafBreakdown.reduce((s, x) => s + x.frac, 0)
        return {
          kind: 'tech' as const,
          ...tech,
          leafBreakdown,
          fracSum,
          isShared: leafBreakdown.length > 1,
        }
      }
      return null
    }
    if (active.type === 'fg') {
      const n = fgNodes.find((x) => x.id === active.id)
      if (!n) return null
      const children = fgNodes.filter((x) => x.parent === active.id)
      const desc = collectDescendantsAll(n.id, fgNodes)
      const topTech = techItems
        .filter((t) => desc.some((d) => (t.attrToFg[d] || 0) > FLOW_THRESHOLD))
        .sort((a, b) => {
          const af = desc.reduce((s, d) => s + (a.attrToFg[d] || 0), 0)
          const bf = desc.reduce((s, d) => s + (b.attrToFg[d] || 0), 0)
          return bf - af
        })
        .slice(0, 8)
      return { kind: 'fg' as const, ...n, children, topTech, descLeaves: desc }
    }
    return null
  }, [active, bioById, techById, bioToTechMap, bioToFgMap, fgTargets, fgNodes, techItems])

  // Net LCA score derived from bio items (pos sum − |neg| sum).
  // Must be declared before the early return below so hook order stays stable.
  const netLcaScore = useMemo(() => {
    if (bioItems.length === 0) return totalScore
    const pos = bioItems.reduce((s, b) => (b.score >= 0 ? s + b.score : s), 0)
    const absNeg = bioItems.reduce((s, b) => (b.score < 0 ? s + Math.abs(b.score) : s), 0)
    const derived = pos - absNeg
    if (
      Math.abs(totalScore) > 1e-12 &&
      Math.abs(derived - totalScore) / Math.abs(totalScore) > 0.05
    ) {
      console.warn('[AttributionSankey] net score mismatch:', derived, '≠', totalScore)
    }
    return derived
  }, [bioItems, totalScore])

  if (!layout) {
    return (
      <div
        style={{
          fontFamily: 'var(--report-font-body)',
          background: 'var(--color-card)',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--color-muted-foreground)',
        }}
      >
        <p style={{ fontSize: 13 }}>
          Attribution data not available — ensure the hotspot result includes{' '}
          <code>attributed_to</code> fields.
        </p>
      </div>
    )
  }

  const {
    bioLayout,
    techLayout,
    nodeLayout,
    bioToUpFlows,
    bypassFlows,
    upToFgFlows,
    treeFlows,
    rootId,
    fgCol,
    fuX,
    zeroLineY,
    negBioIds,
  } = layout
  const rootNode = fgNodes.find((n) => n.id === rootId)
  const rootLabel = rootNode?.label || projectName || 'Activity'
  const fgLevels = Object.keys(fgCol)
    .map(Number)
    .sort((a, b) => b - a)

  return (
    <div
      data-testid="static-attribution-sankey"
      style={{
        fontFamily: 'var(--report-font-body)',
        background: 'var(--color-card)',
        height: '100%',
        color: 'var(--color-foreground)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <style>{`
        .sankey-link {
          display: block;
          padding: 3px 6px;
          margin-left: -6px;
          margin-right: -6px;
          border-radius: 4px;
          transition: background 0.12s, transform 0.12s;
        }
        .sankey-link:hover {
          background: color-mix(in srgb, var(--report-accent) 8%, transparent);
        }
        .sankey-link:active {
          transform: translateX(1px);
        }
        .sankey-link::after {
          content: " ↗";
          opacity: 0.4;
          font-size: 0.85em;
        }
        .sankey-link:hover::after {
          opacity: 1;
        }
      `}</style>
      {/* Toolbar — right-side gutter (paddingRight 290px) keeps the right-aligned
          group (legend chips, Labels button, ✕ clear) to the left of the
          parent SankeyView's Download JSON + Zoom overlays which live in the
          same row at the canvas top-right. */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '6px 290px 6px 10px',
          flexWrap: 'wrap',
          gap: 6,
        }}
      >
        <div>
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--color-foreground)' }}>
            Attribution Sankey
          </span>
          <span style={{ fontSize: 10, color: 'var(--color-muted-foreground)', marginLeft: 10 }}>
            {fmtScore(totalScore)} {totalUnit}
            {methodName ? ` · ${methodName}` : ''}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          {Object.entries(stageColorMap).map(([k, col]) => (
            <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10 }}>
              <div style={{ width: 8, height: 8, borderRadius: 2, background: col }} />
              <span style={{ color: 'var(--color-muted-foreground)' }}>{prettifyStage(k)}</span>
            </div>
          ))}
          <button
            onClick={() => setLabels((l) => !l)}
            style={{
              padding: '3px 8px',
              borderRadius: 4,
              border: `1px solid ${labels ? 'var(--report-accent)' : 'var(--color-border)'}`,
              background: labels
                ? 'color-mix(in srgb, var(--report-accent) 18%, transparent)'
                : 'var(--color-card)',
              color: labels ? 'var(--report-accent)' : 'var(--color-muted-foreground)',
              cursor: 'pointer',
              fontSize: 10,
            }}
          >
            Labels
          </button>
          {active && (
            <button
              onClick={() => {
                setPinned(null)
                setHov(null)
              }}
              style={{
                padding: '3px 8px',
                borderRadius: 4,
                border: '1px solid var(--report-burden)',
                background: 'var(--color-card)',
                color: 'var(--report-burden)',
                cursor: 'pointer',
                fontSize: 10,
              }}
            >
              ✕ clear
            </button>
          )}
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          gap: 10,
          flex: 1,
          minHeight: 0,
          padding: '0 4px 4px',
          overflow: 'hidden',
        }}
      >
        {/* SVG */}
        <div
          style={{
            position: 'relative',
            background: 'var(--report-surface)',
            borderRadius: 10,
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            overflow: 'hidden',
            padding: '0 4px 4px',
          }}
        >
          {netHover && (
            <div
              style={{
                position: 'absolute',
                top: 32,
                left: 8,
                background: 'var(--color-card)',
                border: '1px solid var(--color-border)',
                borderRadius: 6,
                padding: '8px 10px',
                fontSize: 11,
                color: 'var(--color-muted-foreground)',
                lineHeight: 1.5,
                pointerEvents: 'none',
                zIndex: 10,
                maxWidth: 300,
              }}
            >
              <div style={{ fontWeight: 700, color: 'var(--color-foreground)', marginBottom: 4 }}>
                Σ visible biosphere flows = {fmtScore(netLcaScore)} {totalUnit}
              </div>
              <div style={{ marginBottom: 4 }}>
                {((netLcaScore / totalScore) * 100).toFixed(1)}% of total LCA score (
                {fmtScore(totalScore)} {totalUnit})
              </div>
              <div style={{ color: 'var(--color-muted-foreground)', fontSize: 10 }}>
                Top-{techItems.length} background processes drive this view; the remaining{' '}
                {(((totalScore - netLcaScore) / totalScore) * 100).toFixed(1)}% (
                {fmtScore(totalScore - netLcaScore)} {totalUnit}) is via biosphere flows below the
                noise threshold.
              </div>
            </div>
          )}
          <svg
            viewBox={`0 0 ${W} ${H}`}
            preserveAspectRatio="xMidYMid meet"
            style={{ display: 'block', width: '100%', height: '100%' }}
          >
            <defs>
              <pattern
                id="reminder-hatch"
                patternUnits="userSpaceOnUse"
                width="6"
                height="6"
                patternTransform="rotate(45)"
              >
                <rect width="6" height="6" fill="color-mix(in srgb, var(--color-muted-foreground) 6%, transparent)" />
                <line x1="0" y1="0" x2="0" y2="6" stroke="color-mix(in srgb, var(--color-muted-foreground) 28%, transparent)" strokeWidth="2" />
              </pattern>
            </defs>

            {/* ── HEADERS ──────────────────────────────────────────────── */}
            <g>
              <text
                x={BIO_X + BIO_W / 2}
                y={14}
                textAnchor="middle"
                fontSize={10}
                fontWeight={700}
                fill="var(--color-muted-foreground)"
              >
                Elementary flow
              </text>
              <text x={BIO_X + BIO_W / 2} y={26} textAnchor="middle" fontSize={8} fill="var(--color-muted-foreground)">
                biosphere
              </text>
              <line
                x1={BIO_X}
                y1={PAD_T - 4}
                x2={BIO_X + BIO_W}
                y2={PAD_T - 4}
                stroke="var(--color-border)"
                strokeWidth={1}
              />
            </g>
            <g>
              <text
                x={UP_X + UP_W / 2}
                y={14}
                textAnchor="middle"
                fontSize={10}
                fontWeight={700}
                fill="var(--color-muted-foreground)"
              >
                Unit process
              </text>
              <text x={UP_X + UP_W / 2} y={26} textAnchor="middle" fontSize={8} fill="var(--color-muted-foreground)">
                background
              </text>
              <line
                x1={UP_X}
                y1={PAD_T - 4}
                x2={UP_X + UP_W}
                y2={PAD_T - 4}
                stroke="var(--color-border)"
                strokeWidth={1}
              />
            </g>
            {fgLevels.map((lv) => (
              <g key={lv}>
                <text
                  x={(fgCol[lv] ?? 0) + FG_CW / 2}
                  y={14}
                  textAnchor="middle"
                  fontSize={10}
                  fontWeight={700}
                  fill="var(--color-muted-foreground)"
                >
                  Level {lv}
                </text>
                <text
                  x={(fgCol[lv] ?? 0) + FG_CW / 2}
                  y={26}
                  textAnchor="middle"
                  fontSize={8}
                  fill="var(--color-muted-foreground)"
                >
                  {lv === 1 ? 'foreground' : `sub-level ${lv}`}
                </text>
                <line
                  x1={fgCol[lv] ?? 0}
                  y1={PAD_T - 4}
                  x2={(fgCol[lv] ?? 0) + FG_CW}
                  y2={PAD_T - 4}
                  stroke="var(--color-border)"
                  strokeWidth={1}
                />
              </g>
            ))}
            <g>
              <text
                x={fuX + FU_W / 2}
                y={14}
                textAnchor="middle"
                fontSize={10}
                fontWeight={700}
                fill="var(--color-muted-foreground)"
              >
                Functional unit
              </text>
              <line
                x1={fuX}
                y1={PAD_T - 4}
                x2={fuX + FU_W}
                y2={PAD_T - 4}
                stroke="var(--color-border)"
                strokeWidth={1}
              />
            </g>

            {/* fg column lane backgrounds */}
            {fgLevels.map((lv) => (
              <rect
                key={lv}
                x={fgCol[lv] ?? 0}
                y={PAD_T}
                width={FG_CW}
                height={UH}
                fill="color-mix(in srgb, var(--color-muted-foreground) 6%, transparent)"
                rx={2}
              />
            ))}

            {/* ── Z-ORDER: bypass first (behind UP rects) ───────────────── */}
            {bypassFlows.map((f) => {
              const vis = isFlowVis({ id: f.id, bioId: f.bioId, fgSynthId: f.fgSynthId })
              const isNegBio = negBioIds.has(f.bioId)
              // fgSynthId is a synthetic id (__directb__<parent>__bio_<code>);
              // the real fg activity code is on the synth node's parent.
              const fgNode = fgNodes.find((x) => x.id === f.fgSynthId)
              const fgCode = fgNode?.parent ?? null
              const exch = fgCode ? findBioExchange(fgCode, f.bioId) : null
              return (
                <path
                  key={f.id}
                  d={bandPath(
                    BIO_X + BIO_W,
                    f.bioY0,
                    f.bioY1,
                    fgCol[f.fgLevel] ?? fuX,
                    f.fgY0,
                    f.fgY1,
                  )}
                  fill={isNegBio ? 'var(--report-burden)' : 'var(--report-credit)'}
                  stroke={isNegBio ? 'var(--report-burden)' : 'none'}
                  strokeDasharray={isNegBio ? '6 4' : undefined}
                  opacity={vis ? 0.22 : 0.03}
                  style={{ cursor: 'pointer', transition: 'opacity 0.1s' }}
                  onMouseEnter={() => hover('flow', f.id)}
                  onMouseLeave={unhover}
                  onClick={() => (exch ? selectExchange(exch) : click('flow', f.id))}
                />
              )
            })}

            {/* ── upToFg flows ──────────────────────────────────────────── */}
            {upToFgFlows.map((f) => {
              const vis = isFlowVis({ id: f.id, techId: f.techId, fgId: f.fgId })
              const exch = findTechExchange(f.fgId, f.techId)
              return (
                <path
                  key={f.id}
                  d={bandPath(
                    UP_X + UP_W,
                    f.techY0,
                    f.techY1,
                    fgCol[f.fgLevel] ?? fuX,
                    f.fgY0,
                    f.fgY1,
                  )}
                  fill={sc(f.stage)}
                  opacity={vis ? (f.isShared ? 0.48 : 0.28) : 0.03}
                  style={{ cursor: 'pointer', transition: 'opacity 0.1s' }}
                  onMouseEnter={() => hover('flow', f.id)}
                  onMouseLeave={unhover}
                  onClick={() => (exch ? selectExchange(exch) : click('flow', f.id))}
                />
              )
            })}

            {/* ── bioToUp flows ─────────────────────────────────────────── */}
            {bioToUpFlows.map((f) => {
              const vis = isFlowVis({ id: f.id, bioId: f.bioId, techId: f.techId })
              const isNegBio = negBioIds.has(f.bioId)
              const t = techById.get(f.techId)
              return (
                <path
                  key={f.id}
                  d={bandPath(BIO_X + BIO_W, f.bioY0, f.bioY1, UP_X, f.techY0, f.techY1)}
                  fill={isNegBio ? 'var(--report-burden)' : 'var(--report-accent)'}
                  stroke={isNegBio ? 'var(--report-burden)' : 'none'}
                  strokeDasharray={isNegBio ? '6 4' : undefined}
                  opacity={vis ? 0.35 : 0.03}
                  style={{ cursor: 'pointer', transition: 'opacity 0.1s' }}
                  onMouseEnter={() => hover('flow', f.id)}
                  onMouseLeave={unhover}
                  onClick={() =>
                    t ? selectBackgroundActivity(t.id, t.name, t.loc) : click('flow', f.id)
                  }
                />
              )
            })}

            {/* ── tree flows (fg parent→child) ──────────────────────────── */}
            {treeFlows.map((tf) => {
              const vis = isTreeVis(tf)
              const exch = findChildExchange(tf.parentId, tf.childId)
              return (
                <path
                  key={tf.id}
                  d={bandPath(tf.x0, tf.y0t, tf.y0b, tf.x1, tf.y1t, tf.y1b)}
                  fill={sc(tf.stage)}
                  opacity={vis ? 0.2 : 0.04}
                  style={{ transition: 'opacity 0.1s', cursor: exch ? 'pointer' : 'default' }}
                  onClick={() => exch && selectExchange(exch)}
                />
              )
            })}

            {/* ── UP (unit process / tech) rects — paint OVER bypass bands ─ */}
            {techLayout.map((t) => {
              const isAct = active?.type === 'bg' && active.id === t.id
              const dim = active && !isAct && active.type === 'bg'
              const leafC = Object.entries(t.attrToFg).filter(
                ([id, v]) => fgTargets.has(id) && v > FLOW_THRESHOLD,
              )
              const isShared = leafC.length > 1
              const fracSum = leafC.reduce((s, [, v]) => s + v, 0)
              const sortedLC = [...leafC].sort((a, b) => b[1] - a[1])
              let barY = t.y
              return (
                <g
                  key={t.id}
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={() => hover('bg', t.id)}
                  onMouseLeave={unhover}
                  onClick={() => click('bg', t.id)}
                >
                  <rect
                    x={UP_X}
                    y={t.y}
                    width={UP_W}
                    height={t.h}
                    fill={isAct ? 'var(--report-neutral)' : 'var(--color-card)'}
                    opacity={dim ? 0.3 : 1}
                    stroke={isAct ? 'var(--color-muted-foreground)' : 'var(--color-border)'}
                    strokeWidth={0.5}
                    rx={2}
                    style={{ transition: 'opacity 0.1s,fill 0.1s' }}
                  />
                  {sortedLC.map(([fgId, frac]) => {
                    const n = fgNodes.find((x) => x.id === fgId)
                    const bh = fracSum > 0 ? (frac / fracSum) * t.h : 0
                    const el = (
                      <rect
                        key={fgId}
                        x={UP_X + UP_W - 5}
                        y={barY}
                        width={5}
                        height={bh}
                        fill={sc(n?.stage || 'unknown')}
                        opacity={dim ? 0.2 : 0.85}
                        rx={1}
                      />
                    )
                    barY += bh
                    return el
                  })}
                  {isShared && <circle cx={UP_X + 7} cy={t.y + t.h / 2} r={3} fill="var(--color-warning)" />}
                  {labels && t.h > 11 && (
                    <text
                      x={UP_X + (isShared ? 15 : 6)}
                      y={t.y + t.h / 2 - (t.h > 22 ? 5 : 0)}
                      fontSize={Math.min(9, t.h * 0.6)}
                      fill={dim ? 'color-mix(in srgb, var(--color-muted-foreground) 45%, transparent)' : 'var(--color-muted-foreground)'}
                      dominantBaseline="middle"
                      style={{ pointerEvents: 'none' }}
                    >
                      {t.name.length > 21 ? t.name.slice(0, 20) + '…' : t.name}
                    </text>
                  )}
                  {labels && t.h > 22 && (
                    <text
                      x={UP_X + (isShared ? 15 : 6)}
                      y={t.y + t.h / 2 + 7}
                      fontSize={7.5}
                      fill={dim ? 'color-mix(in srgb, var(--color-muted-foreground) 40%, transparent)' : 'var(--color-muted-foreground)'}
                      dominantBaseline="middle"
                      style={{ pointerEvents: 'none' }}
                    >
                      {t.loc} · {fmtScoreSigned(t.score, totalUnit)}
                    </text>
                  )}
                </g>
              )
            })}

            {/* ── BIO (elementary flow) rects ───────────────────────────── */}
            {/* Net annotation top-right of EF column — hover for coverage % */}
            <g
              onMouseEnter={() => setNetHover(true)}
              onMouseLeave={() => setNetHover(false)}
              style={{ cursor: 'help' }}
            >
              <rect
                x={BIO_X + BIO_W - 90}
                y={PAD_T - 14}
                width={90}
                height={12}
                fill="transparent"
              />
              <text
                x={BIO_X + BIO_W - 4}
                y={PAD_T - 6}
                textAnchor="end"
                fontSize={9}
                fill="var(--color-muted-foreground)"
              >
                Net: {fmtScore(netLcaScore)} {totalUnit}
              </text>
            </g>
            {bioLayout.map((bio) => {
              const isNeg = negBioIds.has(bio.id)
              const isResidual = bio.isResidual === true
              const isAct = active?.type === 'bg' && active.id === bio.id
              const dim = active && !isAct && active.type === 'bg'
              // Residual aggregate uses a neutral grey with diagonal hatch so
              // it reads as "long-tail of biosphere flows below top-N".
              // Washes of the diverging data pair (burden/credit) with a
              // neutral wash for the residual long-tail — translucent so they
              // read on both the light and dark plot surfaces.
              const fillBase = isResidual
                ? 'color-mix(in srgb, var(--report-neutral) 15%, transparent)'
                : isNeg
                  ? 'color-mix(in srgb, var(--report-burden) 15%, transparent)'
                  : 'color-mix(in srgb, var(--report-credit) 15%, transparent)'
              const fillAct = isResidual
                ? 'color-mix(in srgb, var(--report-neutral) 30%, transparent)'
                : isNeg
                  ? 'color-mix(in srgb, var(--report-burden) 30%, transparent)'
                  : 'color-mix(in srgb, var(--report-credit) 30%, transparent)'
              const strokeBase = isResidual
                ? 'color-mix(in srgb, var(--report-neutral) 45%, transparent)'
                : isNeg
                  ? 'color-mix(in srgb, var(--report-burden) 45%, transparent)'
                  : 'color-mix(in srgb, var(--report-credit) 45%, transparent)'
              const strokeAct = isResidual
                ? 'var(--report-neutral)'
                : isNeg
                  ? 'var(--report-burden)'
                  : 'var(--report-credit)'
              const labelFill = isResidual
                ? dim
                  ? 'color-mix(in srgb, var(--color-muted-foreground) 45%, transparent)'
                  : 'var(--color-muted-foreground)'
                : isNeg
                  ? dim
                    ? 'color-mix(in srgb, var(--color-muted-foreground) 45%, transparent)'
                    : 'var(--report-burden)'
                  : dim
                    ? 'color-mix(in srgb, var(--color-muted-foreground) 45%, transparent)'
                    : 'var(--report-credit)'
              const subLabelFill = isResidual
                ? dim
                  ? 'color-mix(in srgb, var(--color-muted-foreground) 40%, transparent)'
                  : 'var(--color-muted-foreground)'
                : isNeg
                  ? dim
                    ? 'color-mix(in srgb, var(--color-muted-foreground) 40%, transparent)'
                    : 'var(--report-burden)'
                  : dim
                    ? 'color-mix(in srgb, var(--color-muted-foreground) 40%, transparent)'
                    : 'var(--report-credit)'
              // Split the bio rect into captured (solid, |captured|/|full| × bio.h)
              // and reminder (same fill, hatched overlay) — see ecosemantic-74v.
              const captured = bio.score // signed, = absolute_score
              const full = bio.full // signed
              const reminder = full - captured // signed; long-tail via cols not in (top_N ∪ fg)
              const denom = Math.abs(full) || 1
              const capturedH = (Math.abs(captured) / denom) * bio.h
              const reminderH = (Math.abs(reminder) / denom) * bio.h
              // Char-budget truncation tuned for BIO_W=130 at fontSize ~9.
              const MAX_NAME = 22,
                MAX_SUB = 30
              const nameStr =
                (isNeg ? '−' : '') +
                (bio.name.length > MAX_NAME ? bio.name.slice(0, MAX_NAME - 1) + '…' : bio.name)
              const subFull =
                (bio.compartment ? `[${bio.compartment}] · ` : '') +
                fmtScoreSigned(bio.score, totalUnit)
              const subStr =
                subFull.length > MAX_SUB ? subFull.slice(0, MAX_SUB - 1) + '…' : subFull
              return (
                <g
                  key={bio.id}
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={() => hover('bg', bio.id)}
                  onMouseLeave={unhover}
                  onClick={() => click('bg', bio.id)}
                >
                  {/* captured portion — solid */}
                  <rect
                    x={BIO_X}
                    y={bio.y}
                    width={BIO_W}
                    height={capturedH}
                    fill={isAct ? fillAct : fillBase}
                    opacity={dim ? 0.3 : 1}
                    stroke={isAct ? strokeAct : strokeBase}
                    strokeWidth={0.5}
                    rx={2}
                    style={{ transition: 'opacity 0.1s,fill 0.1s' }}
                  />
                  {/* reminder portion — same fill, hatched overlay */}
                  {reminderH >= 1 && (
                    <g>
                      <rect
                        x={BIO_X}
                        y={bio.y + capturedH}
                        width={BIO_W}
                        height={reminderH}
                        fill={isAct ? fillAct : fillBase}
                        opacity={dim ? 0.3 : 1}
                        stroke={isAct ? strokeAct : strokeBase}
                        strokeWidth={0.5}
                        rx={2}
                      />
                      <rect
                        x={BIO_X}
                        y={bio.y + capturedH}
                        width={BIO_W}
                        height={reminderH}
                        fill="url(#reminder-hatch)"
                        opacity={dim ? 0.3 : 1}
                        rx={2}
                        style={{ pointerEvents: 'none' }}
                      />
                      {reminderH >= 11 && (
                        <text
                          x={BIO_X + 6}
                          y={bio.y + capturedH + reminderH / 2}
                          fontSize={Math.min(8.5, reminderH * 0.55)}
                          fill={subLabelFill}
                          dominantBaseline="middle"
                          style={{ pointerEvents: 'none' }}
                        >
                          {fmtScoreSigned(reminder, totalUnit)}
                        </text>
                      )}
                    </g>
                  )}
                  {isNeg && bio.h > 10 && (
                    <text
                      x={BIO_X + BIO_W - 4}
                      y={bio.y + 9}
                      textAnchor="end"
                      fontSize={9}
                      fill="var(--color-foreground)"
                      style={{ pointerEvents: 'none' }}
                    >
                      ↓
                    </text>
                  )}
                  {labels && capturedH > 11 && (
                    <text
                      x={BIO_X + 6}
                      y={bio.y + Math.min(capturedH, bio.h) / 2 - (capturedH > 22 ? 5 : 0)}
                      fontSize={Math.min(9, capturedH * 0.6)}
                      fill={labelFill}
                      dominantBaseline="middle"
                      style={{ pointerEvents: 'none' }}
                    >
                      {nameStr}
                    </text>
                  )}
                  {labels && capturedH > 22 && (
                    <text
                      x={BIO_X + 6}
                      y={bio.y + Math.min(capturedH, bio.h) / 2 + 7}
                      fontSize={7.5}
                      fill={subLabelFill}
                      dominantBaseline="middle"
                      style={{ pointerEvents: 'none' }}
                    >
                      {subStr}
                    </text>
                  )}
                </g>
              )
            })}
            {/* 0-line separator between positive and negative EF stacks */}
            {negBioIds.size > 0 && (
              <g>
                <line
                  x1={BIO_X}
                  y1={zeroLineY}
                  x2={BIO_X + BIO_W}
                  y2={zeroLineY}
                  stroke="var(--color-muted-foreground)"
                  strokeWidth={1.5}
                />
                <text x={BIO_X + BIO_W + 3} y={zeroLineY + 4} fontSize={8} fill="var(--color-muted-foreground)">
                  0
                </text>
              </g>
            )}

            {/* ── FG node rects (level columns) ─────────────────────────── */}
            {fgNodes
              .filter((n) => n.level > 0)
              .map((n) => {
                const lay = nodeLayout[n.id]
                if (!lay) return null
                const col = sc(n.stage)
                const xPos = fgCol[n.level] ?? fuX
                const isAct = active?.type === 'fg' && active.id === n.id
                const dim = active && !isAct && active.type === 'fg'
                const hasDirect = parentsWithDirect.has(n.id)
                const isCollapsed = effectiveCollapsedParents.has(n.id)
                return (
                  <g
                    key={n.id}
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={() => hover('fg', n.id)}
                    onMouseLeave={unhover}
                    onClick={() => {
                      click('fg', n.id)
                      selectFgActivity(n.id)
                    }}
                  >
                    <rect
                      x={xPos}
                      y={lay.y}
                      width={FG_CW}
                      height={lay.h}
                      fill={col}
                      opacity={dim ? 0.2 : isAct ? 1 : 0.78}
                      rx={3}
                      stroke={isAct ? col : 'none'}
                      strokeWidth={isAct ? 2 : 0}
                      style={{ transition: 'opacity 0.1s' }}
                    />
                    <rect
                      x={xPos}
                      y={lay.y}
                      width={3}
                      height={lay.h}
                      fill="rgba(255,255,255,0.25)"
                      rx={1}
                    />
                    {labels && lay.h > 16 && (
                      <text
                        x={xPos + FG_CW / 2}
                        y={lay.y + lay.h / 2}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fontSize={Math.min(10, lay.h * 0.33)}
                        fill="var(--color-foreground)"
                        fontWeight={700}
                        style={{ pointerEvents: 'none' }}
                      >
                        {n.label.length > 18 ? n.label.slice(0, 17) + '…' : n.label}
                      </text>
                    )}
                    {hasDirect && lay.h >= 22 && (
                      <g
                        onClick={(e) => {
                          e.stopPropagation()
                          toggleCollapsed(n.id)
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        <title>
                          {isCollapsed ? 'Expand direct exchanges' : 'Group direct exchanges'}
                        </title>
                        <rect
                          x={xPos + FG_CW - 26}
                          y={lay.y + 4}
                          width={22}
                          height={18}
                          fill="rgba(0,0,0,0.55)"
                          rx={4}
                          stroke="rgba(255,255,255,0.35)"
                          strokeWidth={0.5}
                        />
                        <text
                          x={xPos + FG_CW - 15}
                          y={lay.y + 17}
                          textAnchor="middle"
                          fontSize={14}
                          fill="#fff"
                          fontWeight={700}
                          style={{ pointerEvents: 'none' }}
                        >
                          {isCollapsed ? '+' : '−'}
                        </text>
                      </g>
                    )}
                  </g>
                )
              })}

            {/* ── FU column ─────────────────────────────────────────────── */}
            <g
              style={{ cursor: rootNode ? 'pointer' : 'default' }}
              onClick={() => rootNode && selectFgActivity(rootNode.id)}
            >
              {fgNodes
                .filter((n) => n.level === 1)
                .map((n) => {
                  const nLay = nodeLayout[n.id]
                  if (!nLay) return null
                  return (
                    <rect
                      key={n.id}
                      x={fuX}
                      y={nLay.y}
                      width={FU_W}
                      height={nLay.h}
                      fill={sc(n.stage)}
                      opacity={0.18}
                    />
                  )
                })}
              <rect
                x={fuX}
                y={PAD_T}
                width={FU_W}
                height={UH}
                fill="none"
                stroke="var(--color-border)"
                strokeWidth={1.5}
                rx={4}
              />
              <text
                x={fuX + FU_W / 2}
                y={PAD_T + UH / 2 - 8}
                textAnchor="middle"
                fontSize={13}
                fill="var(--color-foreground)"
                fontWeight={700}
              >
                {rootLabel.length > 14 ? rootLabel.slice(0, 13) + '…' : rootLabel}
              </text>
              <text
                x={fuX + FU_W / 2}
                y={PAD_T + UH / 2 + 10}
                textAnchor="middle"
                fontSize={9}
                fill="var(--color-muted-foreground)"
              >
                {fmtScore(totalScore)}
              </text>
              <text
                x={fuX + FU_W / 2}
                y={PAD_T + UH / 2 + 24}
                textAnchor="middle"
                fontSize={8}
                fill="var(--color-muted-foreground)"
              >
                {totalUnit}
              </text>
              {rootNode && parentsWithDirect.has(rootNode.id) && (
                <g
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleCollapsed(rootNode.id)
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <title>
                    {effectiveCollapsedParents.has(rootNode.id)
                      ? 'Expand direct exchanges'
                      : 'Group direct exchanges'}
                  </title>
                  <rect
                    x={fuX + FU_W - 28}
                    y={PAD_T + 6}
                    width={22}
                    height={18}
                    fill="rgba(0,0,0,0.55)"
                    rx={4}
                    stroke="rgba(255,255,255,0.35)"
                    strokeWidth={0.5}
                  />
                  <text
                    x={fuX + FU_W - 17}
                    y={PAD_T + 19}
                    textAnchor="middle"
                    fontSize={14}
                    fill="#fff"
                    fontWeight={700}
                    style={{ pointerEvents: 'none' }}
                  >
                    {effectiveCollapsedParents.has(rootNode.id) ? '+' : '−'}
                  </text>
                </g>
              )}
            </g>

            {/* dashed dividers between fg level columns */}
            {fgLevels.map((lv) => (
              <line
                key={lv}
                x1={(fgCol[lv] ?? 0) + FG_CW + 2}
                y1={PAD_T}
                x2={(fgCol[lv] ?? 0) + FG_CW + 2}
                y2={PAD_T + UH}
                stroke="var(--color-border)"
                strokeWidth={1}
                strokeDasharray="3 5"
              />
            ))}
          </svg>
        </div>

        {/* Info panel */}
        <div
          style={{
            width: 195,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            overflowY: 'auto',
            minHeight: 0,
          }}
        >
          {!tooltip && (
            <div
              style={{
                background: 'var(--color-card)',
                borderRadius: 8,
                padding: 12,
                fontSize: 11,
                color: 'var(--color-muted-foreground)',
              }}
            >
              <p style={{ margin: '0 0 6px', fontWeight: 700, color: 'var(--color-muted-foreground)', fontSize: 11 }}>
                Multi-level attribution
              </p>
              <p style={{ margin: '0 0 5px' }}>
                Elementary flows (left) feed into unit processes (UP) or bypass directly to
                foreground activities.
              </p>
              <p style={{ margin: '0 0 5px' }}>
                Teal bands: EF → UP. Green bands: EF bypass → direct emission. Colored bands: UP →
                fg.
              </p>
              <hr style={{ border: 'none', borderTop: '1px solid var(--color-border)', margin: '6px 0' }} />
              <p style={{ margin: 0, color: 'var(--color-muted-foreground)' }}>
                Click any block or flow band to explore.
              </p>
            </div>
          )}
          {tooltip?.kind === 'bio' && (
            <div style={{ background: 'var(--color-card)', borderRadius: 8, padding: 12, fontSize: 11 }}>
              <div style={{ marginBottom: 5 }}>
                <span
                  style={{
                    background: tooltip.score < 0 ? 'color-mix(in srgb, var(--report-burden) 15%, transparent)' : 'color-mix(in srgb, var(--report-credit) 15%, transparent)',
                    color: tooltip.score < 0 ? 'var(--report-burden)' : 'var(--report-credit)',
                    borderRadius: 3,
                    padding: '1px 5px',
                    fontSize: 9,
                    fontWeight: 700,
                  }}
                >
                  EF
                </span>
                {tooltip.compartment && (
                  <span style={{ color: 'var(--color-muted-foreground)', fontSize: 9, marginLeft: 4 }}>
                    {tooltip.compartment}
                  </span>
                )}
              </div>
              <div
                style={{
                  fontWeight: 700,
                  color: 'var(--color-foreground)',
                  marginBottom: 2,
                  fontSize: 12,
                  lineHeight: 1.3,
                }}
              >
                {tooltip.name}
              </div>
              <div style={{ marginBottom: 8, fontSize: 10 }}>
                <span style={{ color: tooltip.score < 0 ? 'var(--report-burden)' : 'var(--color-foreground)' }}>
                  {fmtScore(tooltip.score)} {totalUnit} · {tooltip.pct.toFixed(2)}%
                </span>
              </div>
              {tooltip.score < 0 && tooltip.id !== '__bio_residual__' && (
                <div
                  style={{
                    marginTop: 8,
                    padding: 8,
                    background: 'color-mix(in srgb, var(--report-burden) 15%, transparent)',
                    borderRadius: 4,
                    fontSize: 10,
                    color: 'var(--report-burden)',
                    borderLeft: '3px solid var(--report-burden)',
                  }}
                >
                  <strong>Negative contribution.</strong> The system inventory of this flow is
                  negative because at least one process in the supply chain models an uptake or
                  oxidation of it (e.g. landfill methane oxidation, biogenic carbon sequestration).
                  Multiplied by the positive characterization factor, it offsets positive emissions
                  — this box discounts the total LCA score.
                </div>
              )}
              {tooltip.id === '__bio_residual__' && (
                <div
                  style={{
                    marginTop: 8,
                    padding: 8,
                    background: 'color-mix(in srgb, var(--report-neutral) 15%, transparent)',
                    borderRadius: 4,
                    fontSize: 10,
                    color: 'var(--color-muted-foreground)',
                    borderLeft: '3px solid var(--report-neutral)',
                  }}
                >
                  <strong>Other biosphere flows.</strong> Aggregate of biosphere flows whose
                  individual impact contribution falls below the top-N threshold. This box anchors
                  the EF column to the LCA score so the column total matches the header within
                  rounding.
                </div>
              )}
              {tooltip.techEntries.length > 0 && (
                <>
                  <div
                    style={{
                      color: 'var(--color-muted-foreground)',
                      fontWeight: 700,
                      fontSize: 9,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      marginBottom: 5,
                    }}
                  >
                    Via unit processes
                  </div>
                  {tooltip.techEntries.map((e, i) => {
                    const t = techById.get(e.techId)
                    return (
                      <div
                        key={i}
                        className={t ? 'sankey-link' : undefined}
                        onClick={() => t && selectBackgroundActivity(t.id, t.name, t.loc)}
                        style={{ marginBottom: 2, fontSize: 10, cursor: t ? 'pointer' : 'default' }}
                      >
                        <span style={{ color: t ? 'var(--report-accent)' : 'var(--color-muted-foreground)' }}>
                          {t?.name ?? e.techId}
                        </span>
                        <span style={{ color: 'var(--color-muted-foreground)', marginLeft: 4 }}>
                          {fmtScore(e.score)} {totalUnit}
                        </span>
                      </div>
                    )
                  })}
                </>
              )}
              {tooltip.fgEntries.length > 0 && (
                <>
                  <div
                    style={{
                      color: 'var(--color-muted-foreground)',
                      fontWeight: 700,
                      fontSize: 9,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      marginTop: 6,
                      marginBottom: 5,
                    }}
                  >
                    Direct to foreground
                  </div>
                  {tooltip.fgEntries.map((e, i) => {
                    const n = fgNodes.find((x) => x.id === e.fgSynthId)
                    // fgSynthId is a synthetic id; the real fg activity code
                    // is on the synth node's parent.
                    const fgCode = n?.parent ?? null
                    const exch = fgCode ? findBioExchange(fgCode, tooltip.id) : null
                    return (
                      <div
                        key={i}
                        className={exch ? 'sankey-link' : undefined}
                        onClick={() => exch && selectExchange(exch)}
                        style={{
                          marginBottom: 2,
                          fontSize: 10,
                          cursor: exch ? 'pointer' : 'default',
                        }}
                      >
                        <span
                          aria-hidden="true"
                          style={{
                            display: 'inline-block',
                            width: 8,
                            height: 8,
                            borderRadius: 2,
                            background: sc(n?.stage || 'unknown'),
                            marginRight: 4,
                            verticalAlign: 'baseline',
                          }}
                        />
                        <span style={{ color: 'var(--color-foreground)' }}>
                          {n?.label ?? e.fgSynthId}
                        </span>
                        <span style={{ color: 'var(--color-muted-foreground)', marginLeft: 4 }}>
                          {fmtScore(e.score)} {totalUnit}
                        </span>
                      </div>
                    )
                  })}
                </>
              )}
            </div>
          )}
          {tooltip?.kind === 'tech' && (
            <div style={{ background: 'var(--color-card)', borderRadius: 8, padding: 12, fontSize: 11 }}>
              <div style={{ display: 'flex', gap: 4, marginBottom: 5, flexWrap: 'wrap' }}>
                {tooltip.isShared && (
                  <span
                    style={{
                      background: 'var(--color-warning)',
                      color: '#000',
                      borderRadius: 3,
                      padding: '1px 5px',
                      fontSize: 9,
                      fontWeight: 700,
                    }}
                  >
                    SHARED
                  </span>
                )}
                <span style={{ color: 'var(--color-muted-foreground)', fontSize: 9 }}>unit process</span>
              </div>
              <div
                style={{
                  fontWeight: 700,
                  color: 'var(--color-foreground)',
                  marginBottom: 2,
                  fontSize: 12,
                  lineHeight: 1.3,
                }}
              >
                {tooltip.name}
              </div>
              <div style={{ color: 'var(--color-muted-foreground)', marginBottom: 8, fontSize: 10 }}>
                {tooltip.loc} · {fmtScoreSigned(tooltip.score, totalUnit)} ·{' '}
                {tooltip.pct.toFixed(2)}%
              </div>
              <div
                style={{
                  color: 'var(--color-muted-foreground)',
                  fontWeight: 700,
                  fontSize: 9,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  marginBottom: 5,
                }}
              >
                Attributed to
              </div>
              {tooltip.leafBreakdown.map((x, i) => {
                const fgNode = fgNodes.find((n) => n.id === x.id)
                const fgCode = fgNode?.parent ?? x.id
                const exch = findTechExchange(fgCode, tooltip.id)
                const onRowClick = exch
                  ? () => selectExchange(exch)
                  : () => selectFgActivity(fgCode)
                return (
                  <div
                    key={i}
                    className="sankey-link"
                    onClick={onRowClick}
                    style={{ marginBottom: 4, cursor: 'pointer' }}
                  >
                    <div
                      style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}
                    >
                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4,
                          minWidth: 0,
                          flex: 1,
                        }}
                      >
                        <span
                          aria-hidden="true"
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: 2,
                            background: sc(x.stage),
                            flexShrink: 0,
                          }}
                        />
                        <span
                          style={{
                            color: 'var(--color-foreground)',
                            fontWeight: 600,
                            fontSize: 10,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {x.label}
                        </span>
                      </span>
                      <span style={{ color: 'var(--color-foreground)', fontSize: 10, flexShrink: 0 }}>
                        {(x.frac * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div style={{ background: 'var(--report-grid)', borderRadius: 2, height: 4 }}>
                      <div
                        style={{
                          width: `${(x.frac / tooltip.fracSum) * 100}%`,
                          height: '100%',
                          background: sc(x.stage),
                          borderRadius: 2,
                        }}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          )}
          {tooltip?.kind === 'fg' && (
            <div style={{ background: 'var(--color-card)', borderRadius: 8, padding: 12, fontSize: 11 }}>
              <div
                style={{
                  display: 'inline-block',
                  background: sc(tooltip.stage),
                  color: 'var(--color-foreground)',
                  borderRadius: 3,
                  padding: '1px 6px',
                  fontSize: 9,
                  fontWeight: 700,
                  marginBottom: 5,
                }}
              >
                L{tooltip.level} · {prettifyStage(tooltip.stage)}
              </div>
              <div
                style={{
                  fontWeight: 700,
                  color: 'var(--color-foreground)',
                  marginBottom: 2,
                  fontSize: 12,
                  lineHeight: 1.3,
                }}
              >
                {tooltip.label}
              </div>
              {tooltip.children.length > 0 && (
                <>
                  <div
                    style={{
                      fontSize: 9,
                      color: 'var(--color-muted-foreground)',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      marginBottom: 4,
                      marginTop: 6,
                    }}
                  >
                    Children
                  </div>
                  {tooltip.children.map((c) => {
                    const exch = findChildExchange(tooltip.id, c.id)
                    return (
                      <div
                        key={c.id}
                        className={exch ? 'sankey-link' : undefined}
                        onClick={() => exch && selectExchange(exch)}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          marginBottom: 2,
                          fontSize: 10,
                          cursor: exch ? 'pointer' : 'default',
                        }}
                      >
                        <span
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            minWidth: 0,
                          }}
                        >
                          <span
                            aria-hidden="true"
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: 2,
                              background: sc(c.stage),
                              flexShrink: 0,
                            }}
                          />
                          <span style={{ color: 'var(--color-foreground)' }}>{c.label}</span>
                        </span>
                      </div>
                    )
                  })}
                  <hr style={{ border: 'none', borderTop: '1px solid var(--color-border)', margin: '6px 0' }} />
                </>
              )}
              <div
                style={{
                  fontSize: 9,
                  color: 'var(--color-muted-foreground)',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  marginBottom: 4,
                }}
              >
                Top UP contributors
              </div>
              {tooltip.topTech.map((t, i) => {
                const frac = tooltip.descLeaves.reduce(
                  (s: number, lf: string) => s + (t.attrToFg[lf] || 0),
                  0,
                )
                return (
                  <div
                    key={i}
                    className="sankey-link"
                    onClick={() => selectBackgroundActivity(t.id, t.name, t.loc)}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: 2,
                      fontSize: 10,
                      cursor: 'pointer',
                    }}
                  >
                    <span
                      style={{
                        color: 'var(--report-accent)',
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        paddingRight: 4,
                      }}
                    >
                      {t.name}
                    </span>
                    <span style={{ color: 'var(--color-foreground)', flexShrink: 0 }}>
                      {(frac * 100).toFixed(1)}%
                    </span>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
